import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/useAuth';
import { getStoredToken, apiPost, apiFetch } from '../api';
import { useNotification } from '../context/NotificationContext';

const SessionRevokedModal = () => {
    const { token, user, sid, logout } = useAuth();
    const { addNotification } = useNotification();

    // ── SSA / SESSION_REVOKED state (unchanged) ─────────────────────────────
    const [isOpen, setIsOpen] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const [maxTimeout, setMaxTimeout] = useState(30);
    const [ssaRequestId, setSsaRequestId] = useState(null);
    const [ssaDeviceInfo, setSsaDeviceInfo] = useState('');
    const [ssaIpAddress, setSsaIpAddress] = useState('');
    const [ssaCountry, setSsaCountry] = useState('');
    const [ssaCity, setSsaCity] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [modalType, setModalType] = useState('SSA_REQUEST');

    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);
    const connectingRef = useRef(false);

    // ── WebSocket ───────────────────────────────────────────────────────────
    useEffect(() => {
        let socket = null;
        let reconnectTimeout = null;

        if (!token || !user) {
            setIsOpen(false);
            return;
        }

        const connectWS = () => {
            if (connectingRef.current) return;
            connectingRef.current = true;
            let apiBase = import.meta.env.VITE_API_BASE_URL || window.location.origin;
            if (apiBase.startsWith('/')) apiBase = window.location.origin + apiBase;
            const cleanBase = apiBase.replace(/\/+$/, '').replace(/\/api\/v1$/, '');
            const wsBase = cleanBase.replace(/^http/, 'ws');
            const wsUrl = `${wsBase}/api/v1/notifications/ws?token=${token}`;

            socket = new WebSocket(wsUrl);

            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    if (data.type === 'SSA_LOGIN_REQUEST') {
                        const pendingRequestId = sessionStorage.getItem('ssa_pending_request');
                        if (pendingRequestId && pendingRequestId === data.payload.request_id) {
                            console.log('[SessionRevokedModal] Ignoring SSA_LOGIN_REQUEST — this window is the requester.');
                            return;
                        }
                        const ttl = data.payload.timeout || 30;
                        setSsaRequestId(data.payload.request_id);
                        setSsaDeviceInfo(data.payload.device_info || 'Unknown Device');
                        setSsaIpAddress(data.payload.ip_address || 'Unknown IP');
                        setSsaCountry(data.payload.country_name || '');
                        setSsaCity(data.payload.city || '');
                        setModalType('SSA_REQUEST');
                        setIsProcessing(false);
                        setMaxTimeout(ttl);
                        setCountdown(ttl);
                        setIsOpen(true);

                    } else if (data.type === 'SESSION_REVOKED') {
                        const revokedSid = data.payload?.revoked_sid;
                        if (!revokedSid || revokedSid === sid) {
                            setModalType('SESSION_REVOKED');
                            setMaxTimeout(10);
                            setCountdown(10);
                            setIsOpen(true);
                        }
                    }
                } catch (e) {
                    console.error('WS Message Error:', e);
                }
            };

            socket.onclose = () => {
                connectingRef.current = false;
                if (getStoredToken()) reconnectTimeout = setTimeout(connectWS, 5000);
            };
            socket.onerror = () => socket.close();
        };

        connectWS();
        return () => {
            if (socket) socket.close();
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
        };
    }, [token, user, sid]);

    // ── Local event listeners ───────────────────────────────────────────────
    useEffect(() => {
        // SSA and SESSION_REVOKED logic remains intact
    }, []);

    // ── SSA/SESSION_REVOKED countdown (unchanged logic) ─────────────────────
    useEffect(() => {
        if (!isOpen || isProcessing) return;
        if (countdown <= 0) {
            if (modalType === 'SESSION_REVOKED') logout();
            else if (modalType === 'SSA_REQUEST' && ssaRequestId) handleSsaReject();
            setIsOpen(false);
            return;
        }
        const t = setInterval(() => setCountdown(p => p - 1), 1000);
        return () => clearInterval(t);
    }, [isOpen, countdown, isProcessing, modalType, logout, ssaRequestId]);

    // ── SSA handlers (unchanged) ────────────────────────────────────────────
    const handleSsaApprove = async () => {
        setIsProcessing(true);
        try {
            await apiPost(`/auth/sessions/ssa-approve/${ssaRequestId}`, tokenRef.current, {});
            setIsOpen(false);
            logout();
        } catch {
            setIsProcessing(false);
            setIsOpen(false);
        }
    };

    const handleSsaReject = async () => {
        setIsProcessing(true);
        try {
            await apiPost(`/auth/sessions/ssa-reject/${ssaRequestId}`, tokenRef.current, {});
        } finally {
            setIsProcessing(false);
            setIsOpen(false);
        }
    };

    const pct = isOpen ? Math.max(0, (countdown / (maxTimeout || 10)) * 100) : 0;
    const location = [ssaCity, ssaCountry].filter(Boolean).join(', ');

    return (
        <>
            {/* ── SSA / SESSION_REVOKED blocking modal (existing, unchanged) ── */}
            {isOpen && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-fade-in">

                        {/* Header */}
                        <div className={`px-5 py-4 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 ${
                            modalType === 'SSA_REQUEST' ? 'bg-amber-50 dark:bg-amber-900/20' :
                            'bg-red-50 dark:bg-red-900/20'
                        }`}>
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${
                                modalType === 'SSA_REQUEST' ? 'bg-amber-100 dark:bg-amber-800/40' :
                                'bg-red-100 dark:bg-red-800/40'
                            }`}>
                                {modalType === 'SSA_REQUEST' ? '🔐' : '⚠️'}
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {modalType === 'SSA_REQUEST' ? 'New Login Request' :
                                     'Session Terminated'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {modalType === 'SSA_REQUEST' ? 'Someone is trying to access your account' :
                                     'Your session was ended remotely'}
                                </p>
                            </div>
                        </div>

                        <div className="px-5 py-6">
                            <div className="space-y-4">
                                {modalType === 'SSA_REQUEST' && (
                                    <div className="rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 px-4 py-3 space-y-1.5 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-gray-400">IP Address</span>
                                            <span className="font-mono text-gray-800 dark:text-gray-200">{ssaIpAddress}</span>
                                        </div>
                                        {location && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-500 dark:text-gray-400">Location</span>
                                                <span className="text-gray-800 dark:text-gray-200">{location}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="flex justify-center">
                                    <CountdownRing value={pct} seconds={countdown} isSSA={modalType === 'SSA_REQUEST'} />
                                </div>
                                {isProcessing && (
                                    <div className="h-1 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                        <div className="h-full w-1/3 bg-purple-500 rounded-full animate-[shimmer_1s_linear_infinite]" />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="px-5 pb-5">
                            {modalType === 'SSA_REQUEST' ? (
                                <div className="flex gap-3">
                                    <button onClick={handleSsaReject} disabled={isProcessing}
                                        className="flex-1 py-2.5 text-sm font-semibold rounded-xl border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors">
                                        Block
                                    </button>
                                    <button onClick={handleSsaApprove} disabled={isProcessing}
                                        className="flex-1 py-2.5 text-sm font-semibold rounded-xl bg-primary hover:bg-primary-dark text-white disabled:opacity-50 shadow-md shadow-primary/30 transition-all">
                                        Allow Login
                                    </button>
                                </div>
                            ) : (
                                <button onClick={logout}
                                    className="w-full py-2.5 text-sm font-semibold rounded-xl bg-red-600 hover:bg-red-700 text-white transition-colors">
                                    Log out now
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// SVG countdown ring — unchanged
const CountdownRing = ({ value, seconds, isSSA }) => {
    const r = 22;
    const circ = 2 * Math.PI * r;
    const color = isSSA ? '#f59e0b' : '#ef4444';

    return (
        <div style={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
            <svg width="56" height="56">
                <circle cx="28" cy="28" r={r} fill="none" stroke="currentColor"
                    className="text-gray-200 dark:text-gray-700" strokeWidth="4" />
                <circle cx="28" cy="28" r={r} fill="none"
                    stroke={color}
                    strokeWidth="4"
                    strokeDasharray={circ}
                    strokeDashoffset={circ * (1 - value / 100)}
                    strokeLinecap="round"
                    transform="rotate(-90 28 28)"
                    style={{ transition: 'stroke-dashoffset 1s linear' }}
                />
            </svg>
            <span style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '14px', fontWeight: 700, color,
                lineHeight: 1,
            }}>
                {seconds}
            </span>
        </div>
    );
};

export default SessionRevokedModal;
